import HDDialog from "@/common/components/HDDialog";
import DoctorMappingTable from "@/features/bridge-setup/components/DoctorMappingTable";
import { useDoctorMappingEditor } from "@/features/bridge-setup/components/useDoctorMappingEditor";

interface Props {
    accountId: string | undefined;
    open: boolean;
    onClose: () => void;
}

/**
 * EN-997. The wizard's doctor-mapping table in a modal, for an already-migrated HappyBridge
 * account that still has Bitwerx-era doctor rows. Also used for ezyVet accounts merging existing
 * doctors into synced providers (ezyVet rows never block, so it closes after any save). Unlike the wizard step it never advances on its
 * own: it closes only after a save that leaves no blocking rows, and otherwise stays open showing
 * the refreshed rows.
 */
export default function DoctorMappingModal({ accountId, open, onClose }: Props) {
    const { query, choices, setChoices, keep, setKeep, error, save, blockingLeft, submit } = useDoctorMappingEditor(
        accountId,
        onClose,
    );

    const isEzyvet = query.data?.mode === "ezyvet";

    let body;
    // A failed background refetch keeps the cached table and the clinic's choices on screen.
    if (query.isError && !query.data) {
        body = (
            <div role="alert">
                <p>{"We couldn't load your doctors."}</p>
                <button
                    type="button"
                    className="secondary-button mt-2"
                    onClick={() => void query.refetch()}
                    disabled={query.isFetching}
                >
                    Try again
                </button>
            </div>
        );
    } else if (!query.data || choices === null) {
        body = <p className="text-base text-shadow-300">Loading doctors&hellip;</p>;
    } else {
        body = (
            <>
                <DoctorMappingTable
                    mapping={query.data}
                    choices={choices}
                    keep={keep}
                    onKeepChange={(bridgeDoctorId, doctorId) =>
                        setKeep((prev) => ({ ...prev, [bridgeDoctorId]: doctorId }))
                    }
                    disabled={save.isPending}
                    onChange={(id, c) => setChoices((prev) => ({ ...(prev ?? {}), [id]: c }))}
                />
                {error && (
                    <div role="alert" className="w-full rounded-xl border border-blaze-200 bg-blaze-25 px-5 py-4">
                        <p className="text-base font-semibold text-shadow-700">That didn&rsquo;t save</p>
                        <p className="mt-1 text-base text-shadow-500">{error}</p>
                    </div>
                )}
            </>
        );
    }

    return (
        <HDDialog
            isOpen={open}
            handleClose={onClose}
            header={isEzyvet ? "Match existing doctors" : "Reconcile Cornerstone doctors"}
            subHeader={
                isEzyvet
                    ? "Match each doctor already in HappyDoc to the ezyVet provider it is. Merged doctors keep their visits."
                    : "Match each doctor from your previous Cornerstone connection to its Cornerstone provider."
            }
            className="sm:!max-w-4xl"
            footer={
                <button
                    type="button"
                    className="primary-button"
                    disabled={!query.data || blockingLeft.length > 0 || save.isPending}
                    onClick={submit}
                >
                    Save
                </button>
            }
        >
            {body}
        </HDDialog>
    );
}
